import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Hourglass, Save } from "lucide-react";
import { GracePicker } from "@/components/blacklist/GracePicker";

const STATUS_STYLE = {
  active: "text-amber-300", expired: "text-[var(--ff-red)]", unverified: "text-[var(--ff-text-dim)]",
  left: "text-emerald-400", banned: "text-[var(--ff-red)]", exempt: "text-sky-300",
};
const STATUS_LABEL = {
  active: "Counting down", expired: "Time's up · auto-ban paused", unverified: "Time's up · couldn't verify",
  left: "Left in time", banned: "Auto-banned", exempt: "Exempt (staff / whitelisted)",
};

export function relTime(iso) {
  const ms = new Date(iso) - Date.now();
  const abs = Math.abs(ms), h = Math.floor(abs / 3.6e6), m = Math.floor((abs % 3.6e6) / 6e4);
  const span = h >= 48 ? `${Math.floor(h / 24)}d ${h % 24}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  return ms >= 0 ? `in ${span}` : `${span} ago`;
}

function DefaultGrace({ initial, onSaved }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  const save = async () => {
    try { await api.put("/admin/site-settings", { blacklist_default_grace_hours: val }); toast.success("Default time to leave saved"); onSaved(val); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="grace-default">
      <span className="text-xs text-[var(--ff-text-dim)]">Default for new servers (also used by the <code>/blacklist add</code> Discord command):</span>
      <div className="w-56"><GracePicker key={initial} value={val} onChange={setVal} testId="grace-default-select" compact /></div>
      <button onClick={save} disabled={val === initial} className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-40" data-testid="grace-default-save"><Save size={12} /> Save</button>
    </div>
  );
}

function Row({ r }) {
  return (
    <tr className="border-t border-[var(--ff-border)]" data-testid={`grace-row-${r.id}`}>
      <td className="px-4 py-2">{r.username || "—"} <span className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)]">{r.discord_id}</span></td>
      <td className="px-4 py-2">{r.guild_name}</td>
      <td className="px-4 py-2 text-xs">{new Date(r.deadline).toLocaleString()} <span className="text-[var(--ff-text-dim)]">({relTime(r.deadline)})</span></td>
      <td className={`px-4 py-2 text-xs ${STATUS_STYLE[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}{r.status === "active" && !r.warned ? " · DM failed" : ""}</td>
    </tr>
  );
}

// Countdown board for blacklisted-server grace periods (who must leave, by when).
export default function GraceCountdowns({ reloadKey, onDefaultSaved }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/blacklist-grace").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!data) return null;
  const head = (
    <thead>
      <tr className="bg-[var(--ff-surface)] font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
        <th className="text-left px-4 py-2">Member</th><th className="text-left px-4 py-2">Server</th>
        <th className="text-left px-4 py-2">Deadline</th><th className="text-left px-4 py-2">Status</th>
      </tr>
    </thead>
  );
  return (
    <section className="space-y-3" data-testid="grace-countdowns">
      <div className="label-eyebrow flex items-center gap-2 text-amber-300"><Hourglass size={12} /> ▮ TIME TO LEAVE · {data.open.length} OPEN</div>
      <DefaultGrace initial={data.default_grace_hours} onSaved={(h) => { load(); onDefaultSaved?.(h); }} />
      {data.open.length === 0 ? (
        <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui" data-testid="grace-open-empty">No one is counting down right now.</div>
      ) : (
        <div className="border border-[var(--ff-border)] overflow-x-auto"><table className="w-full text-sm" data-testid="grace-open-table">{head}<tbody>{data.open.map((r) => <Row key={r.id} r={r} />)}</tbody></table></div>
      )}
      {data.recent.length > 0 && (
        <details className="text-sm" data-testid="grace-recent">
          <summary className="cursor-pointer text-xs text-[var(--ff-text-dim)]">Recently resolved ({data.recent.length})</summary>
          <div className="border border-[var(--ff-border)] mt-2 overflow-x-auto"><table className="w-full text-sm">{head}<tbody>{data.recent.map((r) => <Row key={r.id} r={r} />)}</tbody></table></div>
        </details>
      )}
    </section>
  );
}
